import { PermissionGroup } from './types';
import { capitalize } from 'lodash';
import {
  CommonPermission,
  EisPermission,
  RolePermission,
} from 'further-types/admin-user-role';

export const eisPermissionGroups: Array<PermissionGroup> = [
  {
    groupName: 'Dashboards',
    groupDescription: 'Enables visibility of your firm performance over time.',
    groupItems: [
      {
        permissions: [RolePermission.ReadDashboards],
        label: 'View dashboards',
      },
    ],
  },
  {
    groupName: 'My firm',
    groupDescription:
      'Enables the user to view or edit sensitive data such as your firm URL, bank details and contact details.',
    groupItems: [
      {
        label: 'View my firm',
        permissions: [RolePermission.ReadFirm],
        id: 'my-firm-view',
      },
      {
        label: 'Edit my firm',
        requiresId: 'my-firm-view',
        permissions: [RolePermission.EditFirm],
      },
    ],
  },
  {
    groupName: 'Tranches',
    groupDescription:
      'Enables the user to view or edit investment opportunities, adjust fees and documentation.',
    groupItems: [
      {
        label: 'View tranches',
        id: 'tranches-view',
        permissions: [RolePermission.ReadTranche, RolePermission.ReadTag],
        description: 'Includes downloading exports of tranche data',
      },
      {
        label: 'Create new tranches',
        requiresId: 'tranches-view',
        permissions: [
          RolePermission.CreateTranche,
          RolePermission.DuplicateTranche,
        ],
        description: 'Includes the ability to duplicate tranches',
      },
      {
        label: 'Edit existing tranches',
        requiresId: 'tranches-view',
        permissions: [RolePermission.EditTranche],
      },
      {
        label: 'Delete tranches',
        requiresId: 'tranches-view',
        permissions: [RolePermission.DeleteTranche],
      },
    ],
  },
  {
    groupName: 'Investors',
    groupDescription:
      'Enables a user to view investor information and add new investors.',
    groupItems: [
      {
        label: 'View investors',
        id: 'investors-view',
        permissions: [
          RolePermission.ReadInvestor,
          RolePermission.ReadInvestorKyc,
        ],
        description:
          'Includes the ability to view investor KYC data, and export investor data',
      },
      {
        label: 'Create new investors',
        requiresId: 'investors-view',
        permissions: [RolePermission.CreateInvestor],
      },
      {
        label: 'Edit investor data',
        requiresId: 'investors-view',
        permissions: [
          RolePermission.EditInvestor,
          RolePermission.DeleteInvestorCertification,
        ],
        description:
          'Includes on page functionality such as skip cool off, disable emails and add new default adviser',
      },
      {
        label: 'Delete investors',
        requiresId: 'investors-view',
        permissions: [RolePermission.DeleteInvestor],
      },
      {
        label: 'KYC management',
        requiresId: 'investors-view',
        permissions: [RolePermission.EditInvestorKyc],
        description:
          'Enables this user to approve and reset investor KYC statuses',
      },
      {
        label: 'Resend platform invites',
        requiresId: 'investors-view',
        permissions: [RolePermission.CreatePlatformInvites],
        description:
          'Enables the user to trigger the resending of system invites to investors, advisers and organisation users',
      },
      {
        label: 'View investor users',
        requiresId: 'investors-view',
        permissions: [RolePermission.ReadInvestorAccessGrant],
      },
      {
        label: 'Manage investor users',
        requiresId: 'investors-view',
        permissions: [
          RolePermission.EditInvestorAccessGrant,
          RolePermission.CreateInvestorAccessGrant,
          RolePermission.DeleteInvestorAccessGrant,
          RolePermission.EditFurtherUser,
        ],
        description:
          'Includes the ability to add and remove users that are able to access an investor',
      },
    ],
  },
  {
    groupName: 'Subscriptions and payments',
    groupDescription:
      'Enables user to edit subscription amount, edit the adviser, add and remove payments, edit the subcription ID, process freezes and transfers and add interest payments.',
    groupItems: [
      {
        label: 'View subscriptions and incomplete subscriptions',
        id: 'subscriptions-view',
        permissions: [
          RolePermission.ReadInvestment,
          RolePermission.ReadTransfers,
        ],
      },
      {
        label: 'Create new subscriptions',
        requiresId: 'subscriptions-view',
        permissions: [RolePermission.CreateInvestment],
      },
      {
        label: 'Edit commitment data',
        requiresId: 'subscriptions-view',
        id: 'edit-subscription-data',
        permissions: [RolePermission.EditInvestment],
        description:
          'Includes the ability to edit subscription amount, subscription ID, adviser, fee templates and record payments',
      },
      {
        label: 'Delete subscriptions',
        requiresId: 'subscriptions-view',
        permissions: [RolePermission.DeleteInvestment],
      },
      {
        label: 'Bulk payment assignment',
        requiresId: 'edit-subscription-data',
        permissions: [RolePermission.ReadPaymentExpectations],
        description: 'Gives access to the bulk payments assignment tool',
      },
      {
        label: 'Process freezes',
        requiresId: 'subscriptions-view',
        permissions: [RolePermission.CreateInvestmentFreeze],
      },
      {
        label: 'Process transfers',
        requiresId: 'subscriptions-view',
        permissions: [
          RolePermission.TransferInvestment,
          RolePermission.DeleteTransfer,
        ],
      },
      {
        label: 'View interest',
        permissions: [RolePermission.ReadInterest],
      },
      {
        label: 'Upload interest',
        permissions: [RolePermission.UploadInterest],
      },
      {
        label: 'Delete interest uploads',
        permissions: [RolePermission.DeleteInterest],
      },
      {
        label: 'Manage incomplete subscriptions',
        requiresId: 'subscriptions-view',
        permissions: [
          RolePermission.EditIncompleteSubscription,
          RolePermission.DeleteIncompleteSubscription,
        ],
      },
    ],
  },
  {
    groupName: 'Investors and subscriptions bulk upload',
    groupDescription:
      'Enables the tool to bulk-add investors and subscriptions. Please ensure only one type is selected (full or basic).',
    groupItems: [
      {
        label: 'Upload investors and subscriptions (full)',
        permissions: [RolePermission.UploadInvestorFull],
        description:
          'Full upload ability, with adviser dropdown, cash balances, fee overrides, payment statuses and default adviser selection',
      },
      {
        label: 'Upload investors and subscriptions (basic)',
        permissions: [RolePermission.UploadInvestorBasic],
        description:
          'Restricted upload: open tranches only, and adviser selection by email only. Add advisers via Create Adviser if not found.',
      },
    ],
  },
  {
    groupName: 'Fees',
    groupDescription:
      'Enables the creation and edit of fee templates, view and export of charged fees and the addition of one off fees/discounts.',
    groupItems: [
      {
        label: 'View and export fees',
        permissions: [
          RolePermission.ReadCoreFees,
          RolePermission.ReadPerformanceFees,
        ],
        description:
          'Includes the ability to view previously created one-off fees and discounts',
      },
      {
        label: 'Process custodian fee exports',
        permissions: [RolePermission.ExportCustodianFees],
      },
      {
        label: 'Add/edit one-off fees and discounts',
        description:
          'Includes the ability to delete one-off fees and discounts',
        permissions: [
          RolePermission.CreateOneOffFeesAndDiscounts,
          RolePermission.DeleteOneOffFeesAndDiscounts,
        ],
      },
      {
        label: 'Add/edit core fee templates',
        permissions: [
          RolePermission.CreateCoreFees,
          RolePermission.DeleteCoreFees,
        ],
        description: 'Includes deleting unused core fee templates',
      },
      {
        label: 'Add/edit performance fee templates',
        permissions: [
          RolePermission.CreatePerformanceFees,
          RolePermission.DeletePerformanceFees,
        ],
        description: 'Includes deleting unused performance fee templates',
      },
    ],
  },
  {
    groupName: 'Withdrawals',
    groupDescription: 'Enables the creation and review of withdrawals.',
    groupItems: [
      {
        label: 'Review withdrawals',
        permissions: [RolePermission.ReadWithdrawals],
        description: 'Includes the ability to export withdrawals',
      },
      {
        label: 'Request new withdrawals',
        permissions: [RolePermission.RequestWithdrawals],
        description:
          'Requires the below permission to enable these to be accepted',
      },
      {
        label: 'Accept/reject and export withdrawals to custodian',
        permissions: [RolePermission.ExportCustodianWithdrawals],
      },
    ],
  },
  {
    groupName: 'Advisers',
    groupDescription:
      'Enables the creation and review of advisers and organisations.',
    groupItems: [
      {
        label: 'View advisers and organisations',
        id: 'view-advisers-and-organisations',
        permissions: [
          RolePermission.ReadAdvisers,
          RolePermission.ReadOrganisations,
          RolePermission.ReadOrganisationUsers,
        ],
      },
      {
        label: 'Create new advisers and organisations',
        requiresId: 'view-advisers-and-organisations',
        permissions: [
          RolePermission.CreateAdvisers,
          RolePermission.CreateOrganisations,
        ],
      },
      {
        label: 'Edit advisers and organisations',
        requiresId: 'view-advisers-and-organisations',
        permissions: [
          RolePermission.EditAdvisers,
          RolePermission.EditOrganisations,
          RolePermission.CreateOrganisationUsers,
        ],
        description: 'Includes the ability to add new users to organisations',
      },
      {
        label: 'Delete advisers and organisations',
        requiresId: 'view-advisers-and-organisations',
        permissions: [
          RolePermission.DeleteAdvisers,
          RolePermission.DeleteOrganisations,
          RolePermission.DeleteOrganisationUsers,
        ],
        description: 'Includes the ability to remove users from organisations',
      },
    ],
  },
  {
    groupName: 'System exports',
    groupDescription: 'Enables users to export full system data',
    groupItems: [
      {
        label: 'Process system exports',
        permissions: [
          RolePermission.CreateBackgroundExport,
          RolePermission.ReadBackgroundExport,
          RolePermission.DeleteBackgroundExport,
        ],
      },
    ],
  },
  {
    groupName: 'Companies',
    groupDescription:
      'Enables creation of companies, ability to view shareholding data and management of share price ledger.',
    groupItems: [
      {
        label: 'View companies',
        id: 'view-companies',
        permissions: [
          RolePermission.ReadCompanies,
          RolePermission.ReadShareSplit,
        ],
        description:
          'Includes the ability to export company and shareholding data',
      },
      {
        label: 'Create new companies',
        requiresId: 'view-companies',
        permissions: [RolePermission.CreateCompanies],
        description: 'Includes the ability to duplicate companies',
      },
      {
        label: 'Edit company data',
        requiresId: 'view-companies',
        permissions: [
          RolePermission.EditCompanies,
          RolePermission.CreateShareSplit,
          RolePermission.DeleteShareSplit,
        ],
        description:
          'Includes the ability to process share splits, share price changes and dividends',
      },
      {
        label: 'Delete companies',
        requiresId: 'view-companies',
        permissions: [RolePermission.DeleteCompanies],
      },
    ],
  },
  {
    groupName: 'Allocations',
    groupDescription: 'Enables the creation and deletion of allocations.',
    groupItems: [
      {
        label: 'View allocations',
        id: 'view-allocations',
        permissions: [RolePermission.ViewAllocationUpload],
        description: 'Includes the ability to export allocation data',
      },
      {
        label: 'Add new allocations',
        requiresId: 'view-allocations',
        permissions: [RolePermission.CreateAllocationUpload],
      },
      {
        label: 'Edit existing allocations',
        requiresId: 'view-allocations',
        permissions: [RolePermission.EditAllocationUpload],
      },
      {
        label: 'Delete existing allocations',
        requiresId: 'view-allocations',
        permissions: [RolePermission.DeleteAllocationUpload],
      },
    ],
  },
  {
    groupName: 'S/EIS certificates and data',
    groupDescription:
      'Enables the creation and deletion of S/EIS certificates and download of associated S/EIS data.',
    groupItems: [
      {
        label: 'View S/EIS certificate data',
        id: 'view-seis-certificate-data',
        permissions: [RolePermission.ReadSeis],
        description: 'Includes access to the table',
      },
      {
        label: 'Export S/EIS data',
        requiresId: 'view-seis-certificate-data',
        permissions: [RolePermission.DownloadSeis],
        description:
          'Export of table data, S/EIS1 data and S/EIS3 completed files',
      },
      {
        label: 'Add S/EIS certificate and status data',
        requiresId: 'view-seis-certificate-data',
        permissions: [RolePermission.CreateSeis],
        id: 'create-seis',
        description:
          'Includes the ability to notify investors and the ability to mark allocations as ineligible for S/EIS', // TODO: I think the last part, marking allocations as ineligible for S/EIS, is not covered by this permission but instead is to do with the allocations
      },
      {
        label: 'Edit existing S/EIS certificate and status data',
        requiresId: 'view-seis-certificate-data',
        permissions: [RolePermission.EditSeis],
      },
      {
        label: 'Confirm S/EIS status without certificates',
        permissions: [RolePermission.CreateSeisWithoutCertificates],
        requiresId: 'create-seis',
        description:
          'Ability to mark allocations as having had S/EIS if you do not have access to certificate data',
      },
      {
        label: 'Delete existing S/EIS certificate and status data',
        requiresId: 'view-seis-certificate-data',
        permissions: [RolePermission.DeleteSeis],
      },
    ],
  },
  {
    groupName: 'Exits',
    groupDescription: 'Enables the creation and deletion of exits.',
    groupItems: [
      {
        label: 'View exits',
        id: 'view-exits',
        permissions: [RolePermission.ReadExit],
        description: 'Includes the ability to export exit data',
      },
      {
        label: 'Process new exit',
        requiresId: 'view-exits',
        permissions: [RolePermission.CreateExit],
      },
      {
        label: 'Update exit',
        requiresId: 'view-exits',
        permissions: [RolePermission.EditExit],
      },
      {
        label: 'Delete previous exit',
        requiresId: 'view-exits',
        permissions: [RolePermission.DeleteExit],
      },
    ],
  },
  {
    groupName: 'Investor communications',
    groupDescription:
      'Enables the creation, draft creation and deletion of all investor communications.',
    groupItems: [
      {
        label: 'View communications',
        id: 'view-investor-communications',
        permissions: [RolePermission.ReadInvestorCommunications],
        description:
          'Includes the ability to view all investor communications - periodic, custom, deployment and exit statements',
      },
      {
        label: 'Manage periodic update',
        requiresId: 'view-investor-communications',
        id: 'manage-periodic-update',
        permissions: [
          RolePermission.CreateReportPeriodic,
          RolePermission.EditReportPeriodic,
          RolePermission.DeleteReportPeriodic,
        ],
        description:
          'Create, edit and delete periodic updates (does not include the ability to publish)',
      },
      {
        label: 'Publish periodic update',
        requiresId: 'manage-periodic-update',
        permissions: [RolePermission.PublishReportPeriodic],
        description:
          'Allows the user to publish and send periodic updates and edit already published updates',
      },
      {
        label: 'Manage custom update',
        requiresId: 'view-investor-communications',
        id: 'manage-custom-update',
        permissions: [
          RolePermission.CreateReportCustom,
          RolePermission.EditReportCustom,
          RolePermission.DeleteReportCustom,
        ],
        description:
          'Create, edit and delete custom updates (does not include the ability to publish)',
      },
      {
        label: 'Publish custom update',
        requiresId: 'manage-custom-update',
        permissions: [RolePermission.PublishReportCustom],
        description:
          'Allows the user to publish and send custom updates and edit already published updates',
      },
      {
        label: 'Manage deployment update',
        requiresId: 'view-investor-communications',
        permissions: [
          RolePermission.CreateReportDeployment,
          RolePermission.EditReportDeployment,
          RolePermission.DeleteReportDeployment,
        ],
        description: 'Create, edit, delete and publish deployment updates',
      },
      {
        label: 'Manage exit statement',
        requiresId: 'view-investor-communications',
        permissions: [
          RolePermission.CreateReportExitStatement,
          RolePermission.EditReportExitStatement,
          RolePermission.DeleteReportExitStatement,
        ],
        description: 'Create, edit, delete and publish exit statements',
      },
      {
        label: 'Edit and delete investor bulletin',
        permissions: [RolePermission.EditInvestorBulletin],
      },
      {
        label: 'Approve tax reports',
        permissions: [RolePermission.EditTaxReport],
      },
    ],
  },
  {
    groupName: 'Portal impersonation',
    groupDescription: 'Enables the user to launch the portal for a user',
    groupItems: [
      {
        label: 'Impersonate investors',
        permissions: [RolePermission.UseImpersonationAccessInvestor],
      },
      {
        label: 'Impersonate advisers',
        permissions: [RolePermission.UseImpersonationAccessAdviser],
      },
      {
        label: 'Impersonate organisation users',
        permissions: [RolePermission.UseImpersonationAccessOrgUser],
      },
    ],
  },
  {
    groupName: 'User management',
    groupDescription: '',
    groupItems: [
      {
        label: 'View users',
        permissions: [RolePermission.ReadUsers],
        id: 'users-view',
      },
      {
        label: 'Manage users',
        permissions: [
          RolePermission.AddUserToFirm,
          RolePermission.EditUsers,
          RolePermission.RemoveUserFromFirm,
          RolePermission.ReadRole,
        ],
        requiresId: 'users-view',
        description:
          'Includes the ability to add and remove users to and from the firm',
      },
      {
        label: 'Manage user security',
        permissions: [RolePermission.ManageUserSecurity],
        requiresId: 'users-view',
        description: 'Send password reset emails and reset MFA for admin users',
      },
      {
        label: 'Manage roles',
        permissions: [
          RolePermission.ReadRole,
          RolePermission.CreateRole,
          RolePermission.UpdateRole,
          RolePermission.DeleteRole,
        ],
        description: 'Includes the ability to create, edit and delete roles',
      },
    ],
  },
];

const eisRolePermissionsNotIncludedInGroups = [
  ...Object.values(EisPermission),
  ...Object.values(CommonPermission),
].filter(
  (permission) =>
    !eisPermissionGroups.some((group) =>
      group.groupItems.some((item) => item.permissions.includes(permission)),
    ),
);
export const eisNonGroupedPermissions: PermissionGroup = {
  groupName: 'Other permissions',
  groupDescription: 'Permissions that are not grouped into a specific category',
  groupItems: eisRolePermissionsNotIncludedInGroups.map((permission) => ({
    permissions: [permission],
    label: capitalize(permission.replace(':', ' ')),
  })),
};
